import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { getMaintenanceSummary, getCompletionRate, getMaintenanceCosts, getCostPerVehicle, getFuelEfficiencyReport, getDocumentExpiryReport } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { BarChart3, PieChartIcon, TrendingUp, CheckCircle2, Fuel, FileWarning, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#F97316', '#334155', '#10B981', '#F59E0B', '#0EA5E9', '#8B5CF6'];
const STATUS_COLORS = {
    'pending': '#F59E0B',
    'in-progress': '#0EA5E9',
    'completed': '#10B981',
    'cancelled': '#EF4444'
};

export default function ReportsPage() {
    const [summary, setSummary] = useState(null);
    const [completionRate, setCompletionRate] = useState(null);
    const [costData, setCostData] = useState([]);
    const [costPerVehicle, setCostPerVehicle] = useState([]);
    const [fuelEfficiency, setFuelEfficiency] = useState([]);
    const [documentExpiry, setDocumentExpiry] = useState({ expired: [], expiring_soon: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReportData();
    }, []);

    const fetchReportData = async () => {
        try {
            setLoading(true);
            const [summaryRes, completionRes, costRes, costVehicleRes, fuelRes, docRes] = await Promise.all([
                getMaintenanceSummary(),
                getCompletionRate(),
                getMaintenanceCosts(),
                getCostPerVehicle(),
                getFuelEfficiencyReport(),
                getDocumentExpiryReport()
            ]);
            setSummary(summaryRes.data);
            setCompletionRate(completionRes.data);
            setCostData(costRes.data);
            setCostPerVehicle(costVehicleRes.data);
            setFuelEfficiency(fuelRes.data);
            setDocumentExpiry(docRes.data);
        } catch (error) {
            console.error('Failed to fetch report data:', error);
            toast.error('Gagal memuat laporan');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout title="Laporan">
                <LoadingSpinner size="large" />
            </Layout>
        );
    }

    const statusData = summary?.by_status?.map(item => ({
        name: item.status?.charAt(0).toUpperCase() + item.status?.slice(1).replace('-', ' ') || 'Unknown',
        value: item.count,
        color: STATUS_COLORS[item.status] || '#64748B'
    })) || [];

    const categoryData = summary?.by_category?.map(item => ({
        name: item.kategori?.replace('_', ' ') || 'Unknown',
        count: item.count
    })) || [];

    return (
        <Layout title="Laporan">
            <div className="space-y-6" data-testid="reports-page">
                {/* Top Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Completion Rate */}
                    <Card className="industrial-card">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-sm flex items-center justify-center">
                                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                                </div>
                                <div>
                                    <p className="mono-label mb-1">Completion Rate</p>
                                    <p className="text-4xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {completionRate?.rate || 0}%
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {completionRate?.completed || 0} dari {completionRate?.total || 0} selesai
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Document Expiry Alert */}
                    <Card className="industrial-card">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-sm flex items-center justify-center">
                                    <FileWarning className="w-8 h-8 text-orange-600" />
                                </div>
                                <div>
                                    <p className="mono-label mb-1">Dokumen Alert</p>
                                    <p className="text-4xl font-bold text-orange-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        {(documentExpiry?.expired?.length || 0) + (documentExpiry?.expiring_soon?.length || 0)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {documentExpiry?.expired?.length || 0} expired, {documentExpiry?.expiring_soon?.length || 0} akan expired
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Total Cost */}
                    <Card className="industrial-card">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-sm flex items-center justify-center">
                                    <DollarSign className="w-8 h-8 text-blue-600" />
                                </div>
                                <div>
                                    <p className="mono-label mb-1">Total Biaya</p>
                                    <p className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                                        Rp {(costPerVehicle?.reduce((sum, v) => sum + (v.total_biaya || 0), 0) || 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Total maintenance</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cost Trend */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-primary" />
                                Trend Biaya Maintenance
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={costData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                                        <YAxis 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                            tickFormatter={(value) => `${(value / 1000000).toFixed(1)}jt`}
                                        />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '0.25rem'
                                            }}
                                            formatter={(value) => [`Rp ${value.toLocaleString()}`, 'Biaya']}
                                        />
                                        <Line type="monotone" dataKey="biaya" stroke="#F97316" strokeWidth={2} dot={{ fill: '#F97316' }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Status Distribution */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg flex items-center gap-2">
                                <PieChartIcon className="w-5 h-5 text-primary" />
                                Distribusi Status WO
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                {statusData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={statusData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={2}
                                                dataKey="value"
                                            >
                                                {statusData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                contentStyle={{
                                                    backgroundColor: 'hsl(var(--card))',
                                                    border: '1px solid hsl(var(--border))',
                                                    borderRadius: '0.25rem'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <p className="text-muted-foreground">Tidak ada data</p>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-wrap justify-center gap-4 mt-4">
                                {statusData.map((item, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }} />
                                        <span className="text-xs text-muted-foreground">{item.name}: {item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Charts Row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cost per Vehicle */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-primary" />
                                Biaya per Kendaraan
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                {costPerVehicle.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={costPerVehicle.slice(0, 8)} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                            <XAxis 
                                                type="number" 
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}jt`}
                                            />
                                            <YAxis 
                                                dataKey="kendaraan" 
                                                type="category" 
                                                width={100}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                            />
                                            <Tooltip 
                                                contentStyle={{
                                                    backgroundColor: 'hsl(var(--card))',
                                                    border: '1px solid hsl(var(--border))',
                                                    borderRadius: '0.25rem'
                                                }}
                                                formatter={(value) => [`Rp ${value.toLocaleString()}`, 'Biaya']}
                                            />
                                            <Bar dataKey="total_biaya" fill="#F97316" radius={[0, 2, 2, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <p className="text-muted-foreground">Tidak ada data</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Fuel Efficiency */}
                    <Card className="industrial-card">
                        <CardHeader className="border-b border-border pb-4">
                            <CardTitle className="section-title text-lg flex items-center gap-2">
                                <Fuel className="w-5 h-5 text-primary" />
                                Efisiensi BBM per Kendaraan
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="h-[300px]">
                                {fuelEfficiency.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={fuelEfficiency.slice(0, 8)} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                            <XAxis 
                                                type="number" 
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                            />
                                            <YAxis 
                                                dataKey="kendaraan" 
                                                type="category" 
                                                width={100}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                            />
                                            <Tooltip 
                                                contentStyle={{
                                                    backgroundColor: 'hsl(var(--card))',
                                                    border: '1px solid hsl(var(--border))',
                                                    borderRadius: '0.25rem'
                                                }}
                                                formatter={(value) => [`${value} km/L`, 'Efisiensi']}
                                            />
                                            <Bar dataKey="efisiensi_rata" fill="#10B981" radius={[0, 2, 2, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <p className="text-muted-foreground">Tidak ada data</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Document Expiry Table */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <FileWarning className="w-5 h-5 text-primary" />
                            Dokumen Expired / Akan Expired
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {(documentExpiry?.expired?.length > 0 || documentExpiry?.expiring_soon?.length > 0) ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Expired */}
                                <div>
                                    <h4 className="font-semibold text-red-600 mb-3">Sudah Expired</h4>
                                    {documentExpiry?.expired?.length > 0 ? (
                                        <div className="space-y-2">
                                            {documentExpiry.expired.map((doc, index) => (
                                                <div key={index} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-sm border border-red-200 dark:border-red-800">
                                                    <div className="flex justify-between">
                                                        <span className="font-mono text-sm">{doc.kendaraan}</span>
                                                        <span className="text-xs bg-red-200 dark:bg-red-800 px-2 py-0.5 rounded">{doc.dokumen}</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">{doc.tanggal_expired}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">Tidak ada dokumen expired</p>
                                    )}
                                </div>
                                
                                {/* Expiring Soon */}
                                <div>
                                    <h4 className="font-semibold text-orange-600 mb-3">Akan Expired (30 hari)</h4>
                                    {documentExpiry?.expiring_soon?.length > 0 ? (
                                        <div className="space-y-2">
                                            {documentExpiry.expiring_soon.map((doc, index) => (
                                                <div key={index} className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-sm border border-orange-200 dark:border-orange-800">
                                                    <div className="flex justify-between">
                                                        <span className="font-mono text-sm">{doc.kendaraan}</span>
                                                        <span className="text-xs bg-orange-200 dark:bg-orange-800 px-2 py-0.5 rounded">{doc.dokumen}</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">{doc.tanggal_expired} ({doc.hari_tersisa} hari lagi)</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">Tidak ada dokumen akan expired</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="text-center text-muted-foreground py-8">Semua dokumen masih berlaku</p>
                        )}
                    </CardContent>
                </Card>

                {/* Category Distribution */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Distribusi Work Order per Kategori
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="h-[300px]">
                            {categoryData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={categoryData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis 
                                            dataKey="name" 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '0.25rem'
                                            }}
                                        />
                                        <Bar dataKey="count" fill="#334155" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-muted-foreground">Tidak ada data</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
